import React, { useMemo } from 'react'
import { useFragment, graphql } from 'react-relay'

import { getTokenValue, Token, styled } from 'tamagui'

import { HOST_NAME } from '../../data/config/url'
import { Image } from '../Image'

import { EventCoverPhotoNew_event$key } from './__generated__/EventCoverPhotoNew_event.graphql'

const EventCoverPhotoFrame = styled(Image, {
  name: 'EventCoverPhoto',
  objectFit: 'cover',
  overflow: 'hidden',
  aspectRatio: 1,
  flexShrink: 0,
  borderColor: '$borderColor',
  borderWidth: 1,
  backgroundColor: '$background',

  variants: {
    size: {
      '...size': (size, { tokens }) => {
        const sizeToken = tokens.size[size]

        if (
          typeof sizeToken === 'object' &&
          'val' in sizeToken &&
          sizeToken.val > 40
        ) {
          return {
            boxShadow: '0 0 0 3px var(--background)',
            borderRadius: '$4',
          }
        }

        return {
          boxShadow: '0 0 0 1px var(--background)',
          borderRadius: '$2',
        }
      },
    },
  } as const,

  defaultVariants: {
    size: '$5',
  },
} as const)

export const EventCoverPhoto = EventCoverPhotoFrame.styleable<{
  event: EventCoverPhotoNew_event$key
  size: Token
}>(({ event, size = '$5', ...props }, ref) => {
  const data = useFragment(
    graphql`
      fragment EventCoverPhotoNew_event on Event
      @argumentDefinitions(
        imageSize: { type: "Int", defaultValue: 100 }
        dpr: { type: "Int", defaultValue: 3 }
      ) {
        name
        prettyUrl
        updatedAt
        coverPhoto {
          transformUrl(
            transform: { w: $imageSize, h: $imageSize, dpr: $dpr, fit: "cover" }
          )
        }
      }
    `,
    event
  )

  const parsedSize = useMemo(() => {
    return getTokenValue(size, 'size') as number
  }, [size])

  const src =
    data.coverPhoto?.transformUrl ??
    `${HOST_NAME}/social/event/${data.prettyUrl}/card.svg?${data.updatedAt}`

  return (
    <EventCoverPhotoFrame
      {...(props as any)}
      alt={`Cover Photo for ${data.name}`}
      ref={ref}
      size={size}
      src={src}
      width={parsedSize}
      height={parsedSize}
    />
  )
})
